import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useTenantPath } from "@/lib/tenantPath";
import { ArrowLeft, Loader2 } from "lucide-react";

const STATUSES = [
  ["rascunho", "Rascunho"],
  ["a_contactar", "A contactar"],
  ["parcialmente_preenchido", "Parcialmente preenchido"],
  ["confirmado", "Confirmado"],
  ["concluido", "Concluído"],
  ["cancelado", "Cancelado"],
];
const ALL_TAGS = ["hostess", "promotor", "coordenador", "bilingue", "premium", "modelo", "recepção", "experiente"];

export default function EventForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const tPath = useTenantPath();
  const isEdit = Boolean(id);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    name: "", code: "", client_id: "", date: "", start_time: "", end_time: "",
    location: "", slots_needed: 1, profile: "", required_tags: [],
    dress_code: "", briefing: "", hourly_rate: "", travel_allowance: "", status: "rascunho", shifts_enabled: false, carpooling_enabled: false,
  });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    if (isEdit) {
      api.get(`/events/${id}`).then((r) => {
        const e = r.data.event;
        setForm({
          name: e.name || "", code: e.code || "", client_id: e.client_id || "", date: e.date || "",
          start_time: e.start_time || "", end_time: e.end_time || "",
          location: e.location || "", slots_needed: e.slots_needed || 1,
          profile: e.profile || "", required_tags: e.required_tags || [],
          dress_code: e.dress_code || "", briefing: e.briefing || "",
          hourly_rate: e.hourly_rate ?? "", travel_allowance: e.travel_allowance ?? "", status: e.status || "rascunho", shifts_enabled: e.shifts_enabled || false, carpooling_enabled: e.carpooling_enabled || false,
        });
      }).finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const toggleTag = (t) => {
    setForm((f) => ({ ...f, required_tags: f.required_tags.includes(t) ? f.required_tags.filter((x) => x !== t) : [...f.required_tags, t] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    const payload = { ...form, slots_needed: parseInt(form.slots_needed) || 1, hourly_rate: form.hourly_rate === "" ? null : parseFloat(form.hourly_rate), travel_allowance: form.travel_allowance === "" ? null : parseFloat(form.travel_allowance), client_id: form.client_id || null };
    try {
      if (isEdit) await api.put(`/events/${id}`, payload);
      else await api.post("/events", payload);
      navigate(tPath("/eventos"));
    } catch (err) {
      setError(err.response?.data?.detail || "Erro a guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-zinc-500">A carregar…</div>;

  return (
    <div className="max-w-4xl space-y-6" data-testid="event-form-page">
      <Link to={tPath("/eventos")} className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </Link>
      <h1 className="text-3xl font-display font-bold tracking-tight text-zinc-950">{isEdit ? "Editar evento" : "Novo evento"}</h1>

      <form onSubmit={handleSubmit} className="bg-white border border-zinc-200 rounded-xl p-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Nome do evento" required>
            <input data-testid="ev-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="form-input" />
          </Field>
          <Field label="Código / referência">
            <input data-testid="ev-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Ex: EV-2026-001" className="form-input" />
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Cliente">
            <select data-testid="ev-client" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="form-input">
              <option value="">— Sem cliente —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Estado">
            <select data-testid="ev-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="form-input">
              {STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Data" required><input data-testid="ev-date" required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="form-input" /></Field>
          <Field label="Início"><input data-testid="ev-start" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="form-input" /></Field>
          <Field label="Fim"><input data-testid="ev-end" type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="form-input" /></Field>
        </div>
        {form.start_time && form.end_time && (() => {
          const [sh, sm] = form.start_time.split(":").map(Number);
          const [eh, em] = form.end_time.split(":").map(Number);
          return eh * 60 + em < sh * 60 + sm;
        })() && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 -mt-2" data-testid="overnight-hint">
            Este horário termina no dia seguinte (passa da meia-noite). As horas serão calculadas corretamente (ex: 22:00–02:00 = 4h).
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Local"><input data-testid="ev-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="form-input" /></Field>
          <Field label="Nº de vagas" required><input data-testid="ev-slots" required type="number" min="1" value={form.slots_needed} onChange={(e) => setForm({ ...form, slots_needed: e.target.value })} className="form-input" /></Field>
          <Field label="Valor/hora (€)"><input data-testid="ev-rate" type="number" step="0.5" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} className="form-input" /></Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Valor deslocação (€)">
            <input data-testid="ev-travel" type="number" step="0.5" min="0" value={form.travel_allowance} onChange={(e) => setForm({ ...form, travel_allowance: e.target.value })} placeholder="Ex: 5" className="form-input" />
          </Field>
        </div>

        <Field label="Perfil pretendido"><textarea data-testid="ev-profile" rows={2} value={form.profile} onChange={(e) => setForm({ ...form, profile: e.target.value })} className="form-input" /></Field>

        <Field label="Tags requeridas">
          <div className="flex flex-wrap gap-2">
            {ALL_TAGS.map((t) => (
              <button key={t} type="button" onClick={() => toggleTag(t)} data-testid={`ev-tag-${t}`} className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${form.required_tags.includes(t) ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"}`}>
                {t}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Dress code"><input data-testid="ev-dress" value={form.dress_code} onChange={(e) => setForm({ ...form, dress_code: e.target.value })} className="form-input" /></Field>
        <Field label="Briefing"><textarea data-testid="ev-briefing" rows={4} value={form.briefing} onChange={(e) => setForm({ ...form, briefing: e.target.value })} className="form-input" /></Field>

        <div className="border border-zinc-200 rounded-lg p-4 bg-zinc-50/50">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" data-testid="ev-shifts-enabled" checked={form.shifts_enabled} onChange={(e) => setForm({ ...form, shifts_enabled: e.target.checked })} className="w-4 h-4 rounded border-zinc-300" />
            <span>
              <span className="text-sm font-semibold text-zinc-900">Este evento tem vários turnos</span>
              <span className="block text-xs text-zinc-500 mt-0.5">Ativa turnos com horários e vagas próprios. Depois de guardar, geres os turnos e o recrutamento por turno no detalhe do evento.</span>
            </span>
          </label>
        </div>

        <div className="border border-zinc-200 rounded-lg p-4 bg-zinc-50/50">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" data-testid="ev-carpooling-enabled" checked={form.carpooling_enabled} onChange={(e) => setForm({ ...form, carpooling_enabled: e.target.checked })} className="w-4 h-4 rounded border-zinc-300" />
            <span>
              <span className="text-sm font-semibold text-zinc-900">Gestão de boleias</span>
              <span className="block text-xs text-zinc-500 mt-0.5">Cria carros (com condutor, lugares, ponto e hora de encontro) e atribui promotores confirmados. Se o evento tiver turnos, os carros são geridos por turno. Geres tudo no detalhe do evento.</span>
            </span>
          </label>
        </div>

        {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{String(error)}</div>}

        <div className="flex gap-3 pt-2 border-t border-zinc-100">
          <button type="submit" disabled={saving} data-testid="ev-submit" className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-60">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit ? "Guardar alterações" : "Criar evento"}
          </button>
          <Link to={tPath("/eventos")} className="px-5 py-2.5 rounded-lg border border-zinc-200 text-sm hover:bg-zinc-50">Cancelar</Link>
        </div>
      </form>

      <style>{`.form-input{width:100%;padding:0.625rem 0.75rem;border-radius:0.5rem;border:1px solid #e4e4e7;font-size:0.875rem;background:#fff} .form-input:focus{outline:none;border-color:#09090b;box-shadow:0 0 0 2px #09090b}`}</style>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">{label}{required && <span className="text-red-600"> *</span>}</label>
      {children}
    </div>
  );
}
